import React, { useState, useEffect } from "react";
import api from "../api/homeapi";
import { StaffFrame, PageHead, T } from "./DashboardShared";
import { useSettings } from "../context/SettingsContext";
import { ImageIcon, HomeIcon, MoneyIcon, PinIcon, CheckIcon, UploadIcon, FileTextIcon, TrashIcon, PlusIcon } from "../components/Icons";
import { useNotify, errorMessage, InlineAlert } from "../components/Notify";

const css = `
.logo-row{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:12px;}
.logo-prev{height:48px;max-width:150px;object-fit:contain;border:1.5px solid ${T.creamDk};border-radius:10px;background:#fff;padding:4px;}
.logo-empty{height:48px;width:110px;display:flex;align-items:center;justify-content:center;border:1.5px dashed ${T.creamDk};border-radius:10px;font-size:11px;color:${T.smoke};}
.color-field{display:flex;gap:8px;}
.color-field input[type=color]{width:46px;height:42px;border:1.5px solid ${T.creamDk};border-radius:10px;padding:3px;background:#fff;flex-shrink:0;cursor:pointer;}
.tariff-head,.tariff-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) 100px 100px 100px 40px;gap:8px;align-items:center;}
.tariff-head{font-size:10px;font-weight:700;color:${T.smoke};text-transform:uppercase;letter-spacing:.09em;margin-bottom:8px;}
.tariff-row{margin-bottom:8px;}
.tariff-row .tf span{display:none;}
.save-bar{position:sticky;bottom:0;z-index:20;padding:12px 0 calc(12px + env(safe-area-inset-bottom));background:linear-gradient(to top,#eee8da 70%,rgba(238,232,218,0));}
@media(max-width:720px){
  .tariff-head{display:none;}
  .tariff-row{grid-template-columns:1fr 1fr 1fr;grid-template-areas:"from from to" "eco pre vip" "del del del";background:#f7f3ea;border:1.5px solid ${T.creamDk};border-radius:12px;padding:12px;gap:10px;margin-bottom:10px;}
  .tariff-row .tf span{display:block;font-size:10px;font-weight:700;color:${T.smoke};text-transform:uppercase;letter-spacing:.08em;margin-bottom:4px;}
  .tf-from{grid-area:from;}.tf-to{grid-area:to;}.tf-eco{grid-area:eco;}.tf-pre{grid-area:pre;}.tf-vip{grid-area:vip;}
  .tf-del{grid-area:del;justify-self:end;}
  .tariff-row{grid-template-areas:"from from from" "to to to" "eco pre vip" "del del del";}
}
`;

export default function AdminSettings() {
  const notify = useNotify();
  const { refresh: refreshGlobalSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    api.get("/settings")
      .then(r => setForm({ ...r.data, tariffs: r.data.tariffs || [] }))
      .catch(e => setLoadError(errorMessage(e, "Impossible de charger les paramètres actuels.")))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { notify.warning("Choisissez un fichier image (PNG, SVG, JPG…)."); return; }
    if (file.size > 300 * 1024) {
      notify.warning("Image trop lourde (300 Ko maximum). Compressez-la ou utilisez une URL externe.", { title: "Logo refusé" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("logoUrl", reader.result);
    reader.onerror = () => notify.error("Lecture de l'image impossible.");
    reader.readAsDataURL(file);
  };

  const setTariff = (i, key, v) => setForm(p => {
    const tariffs = [...p.tariffs];
    tariffs[i] = { ...tariffs[i], [key]: v };
    return { ...p, tariffs };
  });
  const addTariff = () => setForm(p => ({ ...p, tariffs: [...p.tariffs, { from: "", to: "", eco: null, pre: null, vip: null }] }));
  const removeTariff = async (i) => {
    const t = form.tariffs[i];
    if (t.from || t.to || t.eco || t.pre || t.vip) {
      const ok = await notify.confirm({ title: "Retirer ce trajet de la grille ?", danger: true, confirmLabel: "Retirer",
        message: <>Le tarif <strong>{t.from || "?"} → {t.to || "?"}</strong> sera retiré lors du prochain enregistrement.</> });
      if (!ok) return;
    }
    setForm(p => ({ ...p, tariffs: p.tariffs.filter((_, idx) => idx !== i) }));
  };

  const handleSave = async () => {
    const incomplete = form.tariffs.findIndex(t => !t.from?.trim() || !t.to?.trim());
    if (incomplete >= 0) { notify.warning(`Ligne ${incomplete + 1} de la grille tarifaire : renseignez le départ et l'arrivée.`, { title: "Grille incomplète" }); return; }
    setSaving(true);
    try {
      await api.put("/api/admin/settings", form);
      await refreshGlobalSettings(); // applique immédiatement les nouvelles couleurs/textes sur tout le site
      notify.success("Les paramètres sont enregistrés et appliqués sur le site.", { title: "Paramètres enregistrés" });
    } catch (e) {
      notify.error(errorMessage(e, "Vérifiez votre connexion et réessayez."), { title: "Échec de l'enregistrement" });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <StaffFrame>
        <div className="page narrow">
          <PageHead title="Paramètres" />
          {loadError
            ? <InlineAlert type="error" title="Chargement impossible">{loadError}</InlineAlert>
            : <div className="loading-block"><div className="spinner"/>Chargement des paramètres…</div>}
        </div>
      </StaffFrame>
    );
  }

  return (
    <StaffFrame>
      <style>{css}</style>
      <div className="page narrow">
        <PageHead title="Paramètres" sub="Tout ce qui est modifié ici s'applique immédiatement sur le site public (accueil, tarifs, mentions légales, bande d'annonce)." />

        {/* Identité visuelle */}
        <div className="stf-card">
          <div className="stf-card-title"><ImageIcon size={17}/> Identité visuelle</div>
          <div className="stf-card-sub">Logo et couleurs de la marque.</div>
          <label className="dlabel">Logo</label>
          <div className="logo-row">
            {form.logoUrl ? <img src={form.logoUrl} alt="Logo" className="logo-prev" /> : <div className="logo-empty">Aucun logo</div>}
            <label className="btn btn-ghost" style={{ cursor: "pointer" }}>
              <UploadIcon size={14}/> Téléverser une image
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoFile} />
            </label>
            {form.logoUrl && <button className="btn btn-danger" onClick={() => set("logoUrl", "")}>Retirer</button>}
          </div>
          <label className="dlabel">ou URL du logo</label>
          <input className="dinput" placeholder="https://…/logo.png" value={form.logoUrl && !form.logoUrl.startsWith("data:") ? form.logoUrl : ""} onChange={e => set("logoUrl", e.target.value)} />
          <p className="hint">
            Laissez vide pour garder le logo texte « SETRAG ». Format conseillé : environ <strong>200 × 60 px</strong>, horizontal, fond transparent (PNG ou SVG), idéalement moins de 100 Ko.
          </p>

          <div className="form-grid" style={{ marginTop: 16 }}>
            <div>
              <label className="dlabel">Couleur principale</label>
              <div className="color-field">
                <input type="color" value={form.primaryColor || "#006B3C"} onChange={e => set("primaryColor", e.target.value)} aria-label="Choisir la couleur principale" />
                <input className="dinput" value={form.primaryColor || ""} onChange={e => set("primaryColor", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="dlabel">Couleur secondaire</label>
              <div className="color-field">
                <input type="color" value={form.secondaryColor || "#B8933A"} onChange={e => set("secondaryColor", e.target.value)} aria-label="Choisir la couleur secondaire" />
                <input className="dinput" value={form.secondaryColor || ""} onChange={e => set("secondaryColor", e.target.value)} />
              </div>
            </div>
          </div>
          <InlineAlert type="warning" style={{ marginTop: 14, marginBottom: 0 }}>
            Ces couleurs pilotent la bande d'annonce et les nouvelles pages. La plupart des écrans existants ont encore leurs couleurs fixées dans le code.
          </InlineAlert>
        </div>

        {/* Page d'accueil */}
        <div className="stf-card">
          <div className="stf-card-title"><HomeIcon size={17}/> Page d'accueil</div>
          <div className="form-stack" style={{ marginTop: 12 }}>
            <div>
              <label className="dlabel">Titre principal</label>
              <input className="dinput" value={form.heroTitle || ""} onChange={e => set("heroTitle", e.target.value)} />
            </div>
            <div>
              <label className="dlabel">Sous-titre</label>
              <textarea className="dinput dtextarea" style={{ minHeight: 72 }} value={form.heroSubtitle || ""} onChange={e => set("heroSubtitle", e.target.value)} />
            </div>
          </div>
        </div>

        {/* Tarifs */}
        <div className="stf-card">
          <div className="stf-card-title"><MoneyIcon size={17}/> Grille tarifaire (page Tarifs)</div>
          <div className="stf-card-sub">Prix par trajet et par classe, en FCFA. Laissez VIP vide s'il n'y a pas de VIP sur ce trajet.</div>
          <div className="tariff-head"><span>Départ</span><span>Arrivée</span><span>Éco</span><span>1ère</span><span>VIP</span><span></span></div>
          {form.tariffs.map((t, i) => (
            <div key={i} className="tariff-row">
              <label className="tf tf-from"><span>Départ</span><input className="dinput" placeholder="Libreville" value={t.from || ""} onChange={e => setTariff(i, "from", e.target.value)} /></label>
              <label className="tf tf-to"><span>Arrivée</span><input className="dinput" placeholder="Franceville" value={t.to || ""} onChange={e => setTariff(i, "to", e.target.value)} /></label>
              <label className="tf tf-eco"><span>Éco</span><input className="dinput" type="number" inputMode="numeric" value={t.eco ?? ""} onChange={e => setTariff(i, "eco", e.target.value ? Number(e.target.value) : null)} /></label>
              <label className="tf tf-pre"><span>1ère</span><input className="dinput" type="number" inputMode="numeric" value={t.pre ?? ""} onChange={e => setTariff(i, "pre", e.target.value ? Number(e.target.value) : null)} /></label>
              <label className="tf tf-vip"><span>VIP</span><input className="dinput" type="number" inputMode="numeric" value={t.vip ?? ""} onChange={e => setTariff(i, "vip", e.target.value ? Number(e.target.value) : null)} /></label>
              <button className="icon-btn danger tf-del" onClick={() => removeTariff(i)} aria-label={`Retirer le tarif ${t.from || ""} ${t.to || ""}`} title="Retirer ce trajet"><TrashIcon size={14}/></button>
            </div>
          ))}
          {form.tariffs.length === 0 && <p className="hint" style={{ marginBottom: 10 }}>Aucun trajet dans la grille pour le moment.</p>}
          <button className="btn btn-ghost" onClick={addTariff} style={{ marginTop: 6 }}><PlusIcon size={14}/> Ajouter un trajet</button>
        </div>

        {/* Bande d'annonce */}
        <div className="stf-card">
          <div className="stf-card-title"><PinIcon size={17}/> Bande d'annonce (toutes les pages)</div>
          <div className="form-stack" style={{ marginTop: 12 }}>
            <label className="check-row">
              <input type="checkbox" checked={!!form.bannerActive} onChange={e => set("bannerActive", e.target.checked)} />
              Afficher la bande d'annonce sur le site
            </label>
            <input className="dinput" placeholder="Ex : Trafic perturbé entre Libreville et Franceville le 20 août." value={form.bannerText || ""} onChange={e => set("bannerText", e.target.value)} />
          </div>
        </div>

        {/* Mentions légales */}
        <div className="stf-card">
          <div className="stf-card-title"><FileTextIcon size={17}/> Mentions légales</div>
          <div className="form-stack" style={{ marginTop: 12 }}>
            <div>
              <label className="dlabel">Politique de confidentialité</label>
              <textarea className="dinput dtextarea" style={{ minHeight: 160, fontFamily: "'DM Mono',monospace", fontSize: 13 }} value={form.privacyPolicy || ""} onChange={e => set("privacyPolicy", e.target.value)} />
            </div>
            <div>
              <label className="dlabel">Conditions générales d'utilisation</label>
              <textarea className="dinput dtextarea" style={{ minHeight: 160, fontFamily: "'DM Mono',monospace", fontSize: 13 }} value={form.termsOfService || ""} onChange={e => set("termsOfService", e.target.value)} />
            </div>
          </div>
        </div>

        <div className="save-bar">
          <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving} style={{ padding: 15, fontSize: 15 }}>
            <CheckIcon size={16}/> {saving ? "Enregistrement…" : "Enregistrer les paramètres"}
          </button>
        </div>
      </div>
    </StaffFrame>
  );
}
